import { useState, useEffect, useContext, useRef } from 'react';
import { format, parse, differenceInMinutes, isValid } from 'date-fns';
import { AuthContext } from '../AuthContext';
import { ClockIcon } from '@heroicons/react/20/solid';
import Loading from './loading';
import { useNavigate } from 'react-router-dom';
import Report from './reports';


export default function ShiftStart() {
  const [isLoading, setIsLoading] = useState(false);
  const [shiftStart, setShiftStart] = useState('');
  const [shiftEnd, setShiftEnd] = useState('');
  const [error, setError] = useState(null);
  const [showStartAlert, setShowStartAlert] = useState(false);
  const [showPreShiftSickness, setShowPreShiftSickness] = useState(false);
  const [showEndAlert, setShowEndAlert] = useState(false);
  const [showOverTimeAlert, setShowOverTimeAlert] = useState(false);
  const [showOverTimeOver, setShowOverTimeOver] = useState(false);
  const [dayStatus, setDayStatus] = useState(null);
  const [clockin, setClockin] = useState(null);
  const [hourlyRate, setHourlyRate] = useState(0);
  const [hasShiftToday, setHasShiftToday] = useState(false);
  const [report, setReport] = useState(false);
  const [overtimeData, setOvertimeData] = useState(null);
  const [lateArrival, setLateArrival] = useState(false);
  const [reason, setReason] = useState('');

  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const user = useContext(AuthContext);
  const navigate = useNavigate();

  // Mobile detection
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  // Play notification sound (disabled on mobile for compatibility)
  const playNotificationSound = () => {
    // Skip audio on mobile devices to prevent issues
    if (isMobile || isIOS) {
      console.log('Audio disabled on mobile devices');
      return;
    }

    try {
      if (audioRef.current) {
        audioRef.current.currentTime = 0; // Reset to beginning
        audioRef.current.play().catch((error) => {
          console.log('Audio play failed:', error);
        });
      }
    } catch (error) {
      console.log('Sound notification failed:', error);
    }
  };

  // Create browser notification with sound (mobile-safe)
  const showNotificationWithSound = (title, body) => {
    // Skip sound on mobile
    if (!isMobile && !isIOS) {
      playNotificationSound();
    }
    
    // Skip browser notifications on mobile Safari as they're not reliable
    if (isMobile || isIOS) {
      console.log('Browser notifications disabled on mobile');
      return;
    }

    // Show browser notification if permission granted and not on mobile
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        new Notification(title, {
          body: body,
          icon: '/logo192.png',
          tag: 'shift-alert'
        });
      }
    } catch (error) {
      console.log('Notification API failed:', error);
    }
  };

  // Fetch shift data and set alerts
  const fetchData = async () => {
    try {
      const response = await fetch('https://api.insightos.co.uk/api/myshifts', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch shift data');
      const shiftData = await response.json();

      if (!shiftData.data || shiftData.data.length === 0) {
        setHasShiftToday(false);
        setShowStartAlert(false);
        setShowEndAlert(false);
        setShowOverTimeAlert(false);
        setShowOverTimeOver(false);
        setDayStatus(null);
        return;
      }

      setHasShiftToday(true);

      const shift = shiftData.data[0];
      const HourlyRate = shiftData.staff[0].HourlyRate;
      const shiftStartTime = shift.StartTime;
      const shiftEndTime = shift.EndTime;

      setHourlyRate(HourlyRate);
      setShiftStart(shiftStartTime);
      setShiftEnd(shiftEndTime);

      const now = new Date();
      const currentTime = format(now, 'HH:mm:ss');
      const diffToStart = differenceInMinutes(
        parse(currentTime, 'HH:mm:ss', new Date()),
        parse(shiftStartTime, 'HH:mm:ss', new Date())
      );
      const diffToEnd = differenceInMinutes(
        parse(currentTime, 'HH:mm:ss', new Date()),
        parse(shiftEndTime, 'HH:mm:ss', new Date())
      );

      // Reset all alerts before setting new ones
      setShowStartAlert(false);
      setShowPreShiftSickness(false);
      setShowEndAlert(false);
      setShowOverTimeAlert(false);
      setShowOverTimeOver(false);

      if (Number(shift.Sick) === 1) {
        setDayStatus('sick');
        return;
      }

      if (Number(shift.Holiday) === 1) {
        setDayStatus('holiday');
        return;
      }

      setDayStatus(null);

      // Pre-shift sickness alert (available all day before shift starts)
      if (diffToStart < -5 && shiftData.clockin.length === 0) {
        setShowPreShiftSickness(true);
      }

      // Clock-in logic (1 minute before or at shift start)
      if (diffToStart >= -5 && shiftData.clockin.length === 0) {
        setShowStartAlert(true);
        setShowPreShiftSickness(false); // Hide pre-shift modal when main modal appears
      }

      // Overtime logic
      const renderDetails = shift.OverTime !== null && shift.OverTimeLive === null;
      const overTimeOver = shift.OverTime !== null && shift.OverTimeLive === 1;

      if (
        diffToEnd >= -1 &&
        renderDetails &&
        shiftData.clockin.length > 0 &&
        shiftData.clockin[0].ClockedOut === 0
      ) {
        setClockin(shiftData.clockin[0]);
        setOvertimeData(shift.OverTime);
        setShowOverTimeAlert(true);
      } else if (
        diffToEnd >= -1 &&
        shiftData.clockin.length === 1 &&
        overTimeOver &&
        shiftData.clockin[0].ClockedOut === 0
      ) {
        setClockin(shiftData.clockin[0]);
        setShowOverTimeOver(true);
      } else if (
        diffToEnd >= -1 &&
        shiftData.clockin.length === 1 &&
        shift.OverTime === null &&
        shiftData.clockin[0].ClockedOut === 0
      ) {
        setClockin(shiftData.clockin[0]);
        setShowEndAlert(true);
      }
    } catch (err) {
      setError('Failed to fetch shift data');
    }
  };

  // Fetch data on mount and every 15 minutes
  useEffect(() => {
    // Request notification permission only on desktop
    if (!isMobile && !isIOS && typeof Notification !== 'undefined' && Notification.permission === 'default') {
      try {
        Notification.requestPermission();
      } catch (error) {
        console.log('Notification permission request failed:', error);
      }
    }
    
    fetchData();
    if (hasShiftToday || hasShiftToday === false) {
      const FIFTEEN_MINUTES = 15 * 60 * 1000;
      timerRef.current = setInterval(fetchData, FIFTEEN_MINUTES);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line
  }, [hasShiftToday]);

  // Auto clock out after 2 minutes if shift end alert is shown
  useEffect(() => {
    if (showEndAlert) {
      const timer = setTimeout(() => {
        handleClockOut();
      }, 120000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line
  }, [showEndAlert]);

  // Play sound when start alert appears
  useEffect(() => {
    if (showStartAlert) {
      showNotificationWithSound(
        'Shift Starting Soon',
        'Your scheduled shift is about to start. Please clock in.'
      );
    }
  }, [showStartAlert]);

  // Play sound when end alert appears
  useEffect(() => {
    if (showEndAlert) {
      showNotificationWithSound(
        'Shift Ending Soon',
        'Your scheduled shift is about to end. Please clock out or request overtime.'
      );
    }
  }, [showEndAlert]);

  // Play sound when overtime alert appears
  useEffect(() => {
    if (showOverTimeAlert) {
      showNotificationWithSound(
        'Overtime Available',
        'Overtime is available for your shift. Would you like to continue working?'
      );
    }
  }, [showOverTimeAlert]);

  // Play sound when overtime over alert appears
  useEffect(() => {
    if (showOverTimeOver) {
      showNotificationWithSound(
        'Overtime Period Ending',
        'Your overtime period is ending. Please clock out.'
      );
    }
  }, [showOverTimeOver]);

  // Helper for geolocation (mobile-safe)
  const getGeoTag = () =>
    new Promise((resolve) => {
      // Set a timeout for geolocation on mobile
      const timeout = isMobile || isIOS ? 5000 : 10000;
      
      if (window.electron && window.electron.GeoAPI) {
        window.electron.GeoAPI.getLocation()
          .then((geoTag) => resolve(geoTag))
          .catch(() => resolve('Unknown'));
      } else if (navigator.geolocation) {
        const options = {
          enableHighAccuracy: false, // Use less accurate but faster location on mobile
          timeout: timeout,
          maximumAge: 300000 // 5 minutes cache
        };
        
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            resolve(`${latitude},${longitude}`);
          },
          (error) => {
            console.log('Geolocation failed:', error);
            resolve('Unknown');
          },
          options
        );
      } else {
        resolve('Unknown');
      }
    });

  // Clock in handler
  const handleClockIn = async () => {
    setIsLoading(true);
    try {
      const now = new Date();
      const timeOnly = format(now, 'HH:mm:ss');
      const dateOnly = format(now, 'yyyy-MM-dd');
      const geoTag = await getGeoTag();
      
      // Check if late and calculate minutes late
      const baseDate = new Date();
      const actualTime = parse(format(now, 'HH:mm:ss'), 'HH:mm:ss', baseDate);
      let scheduledTime = parse(shiftStart, 'HH:mm:ss', baseDate);
      if (!isValid(scheduledTime)) {
        scheduledTime = parse(shiftStart, 'HH:mm', baseDate);
      }
      const safeDiff = isValid(actualTime) && isValid(scheduledTime)
        ? differenceInMinutes(actualTime, scheduledTime)
        : 0;
      const isLate = safeDiff > 0;
      const minutesLate = isLate ? safeDiff : 0;

      console.log('MinutesLate debug', {
        shiftStart,
        now: format(now, 'HH:mm:ss'),
        scheduledTime: isValid(scheduledTime) ? format(scheduledTime, 'HH:mm:ss') : 'Invalid',
        actualTime: isValid(actualTime) ? format(actualTime, 'HH:mm:ss') : 'Invalid',
        diffMinutes: safeDiff,
        isLate,
        minutesLate,
      });

      const response = await fetch('https://api.insightos.co.uk/api/clockin/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ClockIn: timeOnly,
          ClockedIn: 1,
          ClockedOut: 0,
          HourlyRate: hourlyRate,
          StaffId: user.id,
          Date: dateOnly,
          Department: user.role,
          StaffName: user.name,
          Late: isLate ? 1 : 0,
          GeoTag: geoTag,
          MinutesLate: minutesLate
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to clock in');
      }

      const result = await response.json();
      setShowStartAlert(false);

      // Handle post-clock-in navigation and late arrival prompt
      const isManagerOrAdmin = user.access === "manager" || user.access === "admin";
      
      if (isManagerOrAdmin) {
        setReport(true);
        setTimeout(() => navigate('/reports'), 500);
        
        // Show late arrival prompt after 15 minutes for managers/admins
        if (isLate) {
          setTimeout(() => setLateArrival(true), 15 * 60 * 1000);
        }
      } else if (user.access === "staff") {
        if (result?.Sick === 1) {
          setTimeout(() => navigate('/back-to-work'), 500);
        } else if (isLate) {
          // Show late arrival prompt immediately for staff
          setTimeout(() => setLateArrival(true), 500);
        }
      }
    } catch (err) {
      setError('Failed to clock in. Please try again.');
      console.error('Clock in error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // if late arrival a prompt appears - with a reason box and submit button
  
  

  // Clock out handler
  const handleClockOut = async () => {
    setIsLoading(true);
    try {
      const geoTag = await getGeoTag();
      if (!clockin || !clockin.ClockIn) throw new Error('No valid shift found for clock-out.');
      const now = new Date();
      const timeOnly = format(now, 'HH:mm:ss');
      const shiftLengthInMinutes = differenceInMinutes(
        parse(timeOnly, 'HH:mm:ss', new Date()),
        parse(clockin.ClockIn, 'HH:mm:ss', new Date())
      );
      const totalPay = (shiftLengthInMinutes / 60) * hourlyRate;
      await fetch(`https://api.insightos.co.uk/api/clockin/addput/${clockin.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ClockOut: timeOnly,
          ClockedOut: 1,
          ClockedIn: 0,
          HoursWorked: shiftLengthInMinutes / 60,
          TotalPay: totalPay.toFixed(2),
          GeoTag: geoTag,
        }),
      });
      setShowEndAlert(false);
      setShowOverTimeAlert(false);
      setShowOverTimeOver(false);
      window.location.reload();
    } catch (err) {
      setError('No Shift Found For This Staff Member, Cannot Clock Out');
    } finally {
      setIsLoading(false);
    }
  };

  // Overtime clock out handler
  const handleClockOutOvertime = async () => {
    setIsLoading(true);
    try {
      const geoTag = await getGeoTag();
      if (!clockin || !clockin.ClockIn) throw new Error('No valid shift found for clock-out.');
      const now = new Date();
      const timeOnly = format(now, 'HH:mm:ss');
      const shiftLengthInMinutes = differenceInMinutes(
        parse(timeOnly, 'HH:mm:ss', new Date()),
        parse(clockin.ClockIn, 'HH:mm:ss', new Date())
      );
      const totalPay = (shiftLengthInMinutes / 60) * hourlyRate;
      await fetch(`https://api.insightos.co.uk/api/clockin/addput/${clockin.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ClockOut: timeOnly,
          ClockedOut: 1,
          ClockedIn: 0,
          HoursWorked: shiftLengthInMinutes / 60,
          TotalPay: totalPay.toFixed(2),
          GeoTag: geoTag,
          ShiftId: shiftEnd?.data?.[0]?.id,
        }),
      });
      setShowOverTimeOver(false);
      setTimeout(() => navigate('/reports'), 500);
    } catch (err) {
      setError('No Shift Found For This Staff Member, Cannot Clock Out');
    } finally {
      setIsLoading(false);
    }
  };

  // Overtime request handler
  const handleOverTime = async () => {
    setIsLoading(true);
    try {
      await fetch('https://api.insightos.co.uk/api/addovertimehours', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ShiftDate: format(new Date(), 'yyyy-MM-dd'),
        }),
      });
      setShowOverTimeAlert(false);
      setTimeout(() => fetchData(), 500);
    } catch (err) {
      setError('Failed to fetch shift data');
    } finally {
      setIsLoading(false);
    }
  };

  // Late arrival reason submission handler
  const handleLateArrivalSubmit = async () => {

    console.log(user.manager)

    setIsLoading(true);
    try {
      await fetch('https://api.insightos.co.uk/api/clockin/addlatearrival', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          Date: format(new Date(), 'yyyy-MM-dd'),
          Reason: reason,
          Manager: user.manager
        }),
      });
      setLateArrival(false);
      setReason('');
      setTimeout(() => fetchData(), 500);
    } catch (err) {
      setError('Failed to submit late arrival reason');
    } finally {
      setIsLoading(false);
    }
  };

  if (lateArrival !== false) {
    return (
      <div className="fixed inset-0 sm:bg-gradient-to-br sm:from-blue-50 sm:via-indigo-50 sm:to-purple-50 sm:flex sm:items-center sm:justify-center sm:p-4 z-50 bg-white overflow-y-auto">
      <div className="bg-white sm:rounded-2xl sm:shadow-2xl w-full max-w-lg sm:border sm:border-gray-100 overflow-hidden min-h-screen sm:min-h-0 flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 px-6 py-6 sm:py-4 flex-shrink-0">
      <div className="flex items-center justify-center">
        <div className="bg-white/20 rounded-full p-3 mr-3">
        <ClockIcon className="h-6 w-6 text-white" />
        </div>
        <div>
        <h2 className="text-xl font-bold text-white">Late Arrival Notice</h2>
        <p className="text-orange-100 text-sm">Action Required</p>
        </div>
      </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6 flex-1 flex flex-col justify-between">
      <div>
        <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
        <span className="text-2xl">⚠️</span>
        </div>
        <p className="text-gray-700 text-base leading-relaxed">
        Hi <span className="font-semibold text-gray-900">{user.name}</span>, 
        you're clocking in later than your scheduled time. Please provide a brief explanation for your late arrival.
        </p>
        </div>

        <div className="space-y-4">
        <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
        Reason for Late Arrival <span className="text-red-500">*</span>
        </label>
        <textarea
        className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-100 transition-all duration-200 resize-none text-gray-700 placeholder-gray-400"
        rows="4"
        placeholder="e.g., Traffic delay, public transport issue, medical appointment..."
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        maxLength={500}
        />
        <div className="flex justify-between items-center mt-2">
        <span className="text-xs text-gray-500">Be specific but concise</span>
        <span className="text-xs text-gray-400">{reason.length}/500</span>
        </div>
        </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 pt-6 mt-auto">
        <button
        data-component="late-arrival-modal"
        data-time-saved="5"
        data-feature="cancel-late-arrival"
        onClick={() => {
        setLateArrival(false);
        setReason('');
        }}
        className="w-full sm:flex-1 px-4 py-3 border-2 border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-gray-100"
        >
        Cancel
        </button>
        <button
        data-component="late-arrival-modal"
        data-feature="submit-late-arrival"
        onClick={handleLateArrivalSubmit}
        disabled={isLoading || !reason.trim()}
        className="w-full sm:flex-1 px-4 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-red-600 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-orange-200 shadow-lg"
        >
        {isLoading ? (
        <div className="flex items-center justify-center">
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Submitting...
        </div>
        ) : (
        'Submit Explanation'
        )}
        </button>
      </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-50 px-6 py-3 border-t border-gray-100 flex-shrink-0">
      <p className="text-xs text-gray-500 text-center">
        This information will be recorded for attendance tracking purposes.
      </p>
      </div>
      </div>
      </div>
    );
  }

  if (report) return <Report />;

  return (
    <>
      {/* Audio element for notification sound - only load on desktop */}
      {!isMobile && !isIOS && (
        <audio
          ref={audioRef}
          preload="none"
          style={{ display: 'none' }}
        >
          <source src="/sound.mp3" type="audio/mpeg" />
          Your browser does not support the audio element.
        </audio>
      )}
      
      {isLoading && <Loading />}
      {error && (
        <div className="fixed top-4 left-4 right-4 z-50 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      <div
        aria-live="assertive"
        className="pointer-events-none fixed inset-0 flex items-end px-3 py-4 sm:items-start sm:p-6 z-50"
      >
        <div className="flex w-full flex-col items-center space-y-3 sm:items-end">
          {dayStatus && (
            <div className={`pointer-events-auto flex w-full max-w-sm sm:max-w-md mx-auto overflow-hidden rounded-xl border shadow-2xl ${dayStatus === 'sick' ? 'border-rose-200 bg-gradient-to-br from-rose-600 to-red-700' : 'border-sky-200 bg-gradient-to-br from-sky-600 to-indigo-700'}`}>
              <div className="flex-1 p-5 text-white sm:p-6">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white/20 text-2xl" aria-hidden="true">
                    {dayStatus === 'sick' ? '🤒' : '🏖️'}
                  </div>
                  <div>
                    <p className="text-lg font-bold">
                      {dayStatus === 'sick' ? 'Sick Today' : 'Holiday Today'}
                    </p>
                    <p className="mt-1 text-sm text-white/85">
                      {dayStatus === 'sick'
                        ? 'You are recorded as sick today. You do not need to clock in.'
                        : 'You are on holiday today. You do not need to clock in.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          {showStartAlert && (
            <div className="pointer-events-auto flex w-full max-w-sm sm:max-w-md mx-auto bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden">
              <div className="flex-1 p-4 sm:p-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-10 w-10 rounded-full bg-green-100">
                      <ClockIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
                    </div>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-semibold text-gray-900">
                      Hello {user.name}! 👋
                    </p>
                    <p className="mt-1 text-sm text-gray-600">
                      Your shift is starting now. Ready to clock in?
                    </p>
                    <div className="mt-4">
                      <button
                        data-component="shift-start-alert"
                        data-feature="clock-in-now"
                        onClick={handleClockIn}
                        type="button"
                        disabled={isLoading}
                        className="w-full inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transition-all duration-200"
                      >
                        {isLoading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Clocking In...
                          </>
                        ) : (
                          <>
                            <ClockIcon className="h-4 w-4 mr-2" />
                            Clock In Now
                          </>
                        )}
                      </button>
                      <button
                        data-component="shift-start-alert"
                        data-feature="report-sickness"
                        onClick={() => navigate('/sickai')}
                        type="button"
                        className="w-full mt-3 inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 shadow-lg transition-all duration-200"
                      >
                        🤒 Report Sickness
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {showPreShiftSickness && (
            <div className="pointer-events-auto flex w-full max-w-sm mx-auto bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="flex-1 p-3">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-8 w-8 rounded-full bg-orange-100">
                      <span className="text-orange-600 text-sm">🤒</span>
                    </div>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      Need to report sickness?
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Shift starts at {shiftStart}
                    </p>
                    <button
                      data-component="shift-pre-sickness"
                      data-feature="report-sickness"
                      onClick={() => navigate('/sickai')}
                      type="button"
                      className="mt-2 w-full inline-flex items-center justify-center px-3 py-2 border border-transparent text-xs font-medium rounded-lg text-white bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 shadow-md transition-all duration-200"
                    >
                      Report Sickness
                    </button>
                  </div>
                  <div className="ml-2">
                    <button
                      onClick={() => setShowPreShiftSickness(false)}
                      type="button"
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <span className="sr-only">Close</span>
                      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {showEndAlert && (
            <div className="pointer-events-auto flex w-full max-w-sm sm:max-w-md mx-auto bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden">
              <div className="flex-1 p-4 sm:p-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-10 w-10 rounded-full bg-red-100">
                      <ClockIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                    </div>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-semibold text-gray-900">
                      Shift Ending {user.name}! ⏰
                    </p>
                    <p className="mt-1 text-sm text-gray-600">
                      Your shift is ending. Time to clock out!
                    </p>
                    <div className="mt-4">
                      <button
                        data-component="shift-end-alert"
                        data-feature="clock-out-now"
                        onClick={handleClockOut}
                        type="button"
                        disabled={isLoading}
                        className="w-full inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transition-all duration-200"
                      >
                        {isLoading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Clocking Out...
                          </>
                        ) : (
                          <>
                            <ClockIcon className="h-4 w-4 mr-2" />
                            Clock Out Now
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {showOverTimeAlert && (
            <div className="pointer-events-auto flex w-full max-w-sm sm:max-w-md mx-auto bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden">
              <div className="flex-1 p-4 sm:p-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-10 w-10 rounded-full bg-yellow-100">
                      <ClockIcon className="h-6 w-6 text-yellow-600" aria-hidden="true" />
                    </div>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-semibold text-gray-900">
                      Overtime Available! 💰
                    </p>
                    <p className="mt-1 text-sm text-gray-600">
                      Work an extra {overtimeData} hours overtime?
                    </p>
                    <div className="mt-4 space-y-2">
                      <button
                        data-component="shift-overtime-alert"
                        data-feature="accept-overtime"
                        onClick={handleOverTime}
                        type="button"
                        disabled={isLoading}
                        className="w-full inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transition-all duration-200"
                      >
                        {isLoading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Starting Overtime...
                          </>
                        ) : (
                          <>
                            ✅ Yes, Continue Working
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleClockOut}
                        type="button"
                        disabled={isLoading}
                        className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      >
                        ❌ No, Clock Out
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {showOverTimeOver && (
            <div className="pointer-events-auto flex w-full max-w-sm sm:max-w-md mx-auto bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden">
              <div className="flex-1 p-4 sm:p-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-10 w-10 rounded-full bg-blue-100">
                      <ClockIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />
                    </div>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-semibold text-gray-900">
                      Overtime Complete! 🎉
                    </p>
                    <p className="mt-1 text-sm text-gray-600">
                      Great work {user.name}! Time to clock out.
                    </p>
                    <div className="mt-4">
                      <button
                        onClick={handleClockOutOvertime}
                        type="button"
                        disabled={isLoading}
                        className="w-full inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transition-all duration-200"
                      >
                        {isLoading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Clocking Out...
                          </>
                        ) : (
                          <>
                            <ClockIcon className="h-4 w-4 mr-2" />
                            Clock Out
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}